import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Alert, InputGroup } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";

const EXTRA_EMAIL_FIELDS = ["email2", "email3", "email4", "email5"];

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function EmailDocumentModal({
  show,
  onHide,
  title = "Send Document",
  defaultEmail,
  email2,
  email3,
  email4,
  email5,
  helperText = "The document will be sent as a PDF attachment to all email addresses entered above.",
  onSend, // (emailsArray) => Promise | void
}) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [extraEmails, setExtraEmails] = useState({
    email2: undefined,
    email3: undefined,
    email4: undefined,
    email5: undefined,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const defaultsMap = useMemo(
    () => ({
      email2: (email2 || "").trim(),
      email3: (email3 || "").trim(),
      email4: (email4 || "").trim(),
      email5: (email5 || "").trim(),
    }),
    [email2, email3, email4, email5]
  );

  useEffect(() => {
    if (!show) return;

    setErrorMessage("");
    setRecipientEmail((defaultEmail || "").trim());

    setExtraEmails({
      email2: defaultsMap.email2 ? defaultsMap.email2 : undefined,
      email3: defaultsMap.email3 ? defaultsMap.email3 : undefined,
      email4: defaultsMap.email4 ? defaultsMap.email4 : undefined,
      email5: defaultsMap.email5 ? defaultsMap.email5 : undefined,
    });
  }, [show, defaultEmail, defaultsMap]);

  const handleAddExtraField = () => {
    const nextField = EXTRA_EMAIL_FIELDS.find((f) => extraEmails[f] === undefined);
    if (!nextField) return;
    setExtraEmails((prev) => ({ ...prev, [nextField]: "" }));
  };

  const handleRemoveExtraField = (field) => {
    setExtraEmails((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleExtraChange = (field, value) => {
    setExtraEmails((prev) => ({ ...prev, [field]: value }));
  };

  const allExtraUsed = EXTRA_EMAIL_FIELDS.every((f) => extraEmails[f] !== undefined);

  const handleSend = async () => {
    setErrorMessage("");

    const emails = [
      (recipientEmail || "").trim(),
      ...EXTRA_EMAIL_FIELDS.map((f) => (extraEmails[f] || "").trim()),
    ].filter(Boolean);

    if (!emails.length) {
      setErrorMessage("At least one email address is required.");
      return;
    }

    const invalid = emails.filter((e) => !isValidEmail(e));
    if (invalid.length) {
      setErrorMessage(`These email(s) look invalid: ${invalid.join(", ")}`);
      return;
    }

    // close immediately (same UX you already like)
    onHide();

    try {
      await onSend?.(emails);
    } catch (err) {
      console.error("Failed to send document email:", err);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        )}

        <Form>
          <Form.Group controlId="formGridRecipientEmail">
            <Form.Label>Email</Form.Label>
            <InputGroup className="align-items-center">
              <Form.Control
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="Recipient email"
              />
              <Button
                variant="link"
                className="text-primary d-flex align-items-center"
                onClick={handleAddExtraField}
                disabled={allExtraUsed}
                title={allExtraUsed ? "Maximum emails added" : "Add another email"}
              >
                <FaPlus />
              </Button>
            </InputGroup>
          </Form.Group>

          {EXTRA_EMAIL_FIELDS.map((field, idx) => {
            if (extraEmails[field] === undefined) return null;
            return (
              <Form.Group key={field} className="mt-2">
                <InputGroup className="align-items-center">
                  <Form.Control
                    type="email"
                    value={extraEmails[field]}
                    placeholder={`Additional Email ${idx + 2}`}
                    onChange={(e) => handleExtraChange(field, e.target.value)}
                  />
                  <Button
                    variant="link"
                    className="text-danger ms-0 d-flex align-items-center"
                    onClick={() => handleRemoveExtraField(field)}
                    title="Remove"
                  >
                    <FaTrash />
                  </Button>
                </InputGroup>
              </Form.Group>
            );
          })}

          <p className="mt-3 mb-0">{helperText}</p>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" className="rounded-0" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" className="rounded-0" onClick={handleSend}>
          Send
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
